import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek, addMonths, subMonths } from 'date-fns';
import { CalendarEvent, CalendarViewMode } from '../scheduling/types';
import { supabase } from '@/integrations/supabase/client';
import AppLayout from '@/components/layout/AppLayout';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CalendarStandardView from '../scheduling/components/CalendarStandardView';
import CalendarViewSwitcher from '../scheduling/components/CalendarViewSwitcher';
import CalendarToolbar from '../scheduling/components/CalendarToolbar';
import { useUser } from '@/contexts/UserContext';
import { useManagerPermissions } from '@/hooks/useManagerPermissions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import GuardViewModeMenu from './components/GuardViewModeMenu';
import { CalendarDays } from 'lucide-react';
import { ColorIntensityProvider } from '../scheduling/contexts/ColorIntensityContext';

const GuardTeamScheduleContent = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, roles } = useUser();
  const isGuard = roles?.includes('guard');
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<CalendarViewMode>('week');
  const [schedules, setSchedules] = useState<CalendarEvent[]>([]);
  const [filteredSchedules, setFilteredSchedules] = useState<CalendarEvent[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  
  // Get manager permissions for the selected team
  const { canManageTeam } = useManagerPermissions(selectedTeamId);
  
  const calculateDateRange = (date: Date, view: CalendarViewMode) => {
    let start, end;
    
    if (isGuard) {
      start = startOfWeek(date, { weekStartsOn: 1 });
      end = endOfWeek(date, { weekStartsOn: 1 });
      return { start, end };
    }
    
    switch (view) {
      case 'day':
        start = new Date(date);
        end = new Date(date);
        break;
      case 'week':
        start = startOfWeek(date, { weekStartsOn: 1 });
        end = endOfWeek(date, { weekStartsOn: 1 });
        break;
      case 'month':
        start = new Date(date.getFullYear(), date.getMonth(), 1);
        end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        break;
      case 'custom':
        start = new Date(date);
        start.setDate(date.getDate() - 5);
        end = new Date(date);
        end.setDate(date.getDate() + 5);
        break;
      case 'total-work':
        start = startOfWeek(date, { weekStartsOn: 1 });
        end = endOfWeek(date, { weekStartsOn: 1 });
        break;
      default:
        start = startOfWeek(date, { weekStartsOn: 1 });
        end = endOfWeek(date, { weekStartsOn: 1 });
    }
    
    return { start, end };
  };
  
  const dateRange = calculateDateRange(currentDate, viewMode);
  const isCustomView = viewMode === 'custom';
  
  const { data: guardSchedules = [], isLoading: isLoadingGuardSchedules } = useQuery({
    queryKey: ['guardSchedules', dateRange],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('team_schedules')
          .select('*')
          .gte('start_date', dateRange.start.toISOString())
          .lte('start_date', dateRange.end.toISOString());
          
        if (error) throw error;
        
        const schedulesWithGuards = await Promise.all(
          (data || []).map(async (schedule) => {
            let guardNames: string[] = [];
            
            if (schedule.assigned_guards && Array.isArray(schedule.assigned_guards)) {
              const { data: guards } = await supabase
                .from('profiles')
                .select('full_name, first_name, last_name')
                .in('id', schedule.assigned_guards);
                
              guardNames = (guards || []).map(guard => 
                guard.full_name || `${guard.first_name || ''} ${guard.last_name || ''}`.trim()
              );
            }
            
            return {
              ...schedule,
              guard_names: guardNames
            };
          })
        );
        
        return schedulesWithGuards;
      } catch (error) {
        console.error('Error fetching schedules:', error);
        return [];
      }
    },
    enabled: true
  });
  
  useEffect(() => {
    setSchedules(guardSchedules);
    setFilteredSchedules(guardSchedules);
  }, [guardSchedules]);
  
  const handleGoToToday = () => {
    setCurrentDate(new Date());
  };
  
  const handleNavigatePrevious = () => {
    if (isGuard) {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() - 1);
        return newDate;
      });
      return;
    }
    
    if (viewMode === 'day') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() - 1);
        return newDate;
      });
    } else if (viewMode === 'week') {
      setCurrentDate(prev => subWeeks(prev, 1));
    } else if (viewMode === 'month') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() - 1);
        return newDate;
      });
    } else if (viewMode === 'custom') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() - 10);
        return newDate;
      });
    } else if (viewMode === 'total-work') {
      setCurrentDate(prev => subWeeks(prev, 1));
    }
  };
  
  const handleNavigateNext = () => {
    if (isGuard) {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() + 1);
        return newDate;
      });
      return;
    }
    
    if (viewMode === 'day') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() + 1);
        return newDate;
      });
    } else if (viewMode === 'week') {
      setCurrentDate(prev => addWeeks(prev, 1));
    } else if (viewMode === 'month') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() + 1);
        return newDate;
      });
    } else if (viewMode === 'custom') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() + 10);
        return newDate;
      });
    } else if (viewMode === 'total-work') {
      setCurrentDate(prev => addWeeks(prev, 1));
    }
  };
  
  const handleDayClick = (date: Date) => {
    console.log('Day clicked:', date);
  };
  
  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent, isMultiSelectMode?: boolean) => {
    console.log('Event clicked:', event);
  };
  
  const handleDragEnd = (result: any) => {
    console.log('Drag end:', result);
  };
  
  const handleDuplicateEvent = (event: CalendarEvent) => {
    console.log('Duplicate event:', event);
  };
  
  const handleDeleteEvent = (event: CalendarEvent) => {
    console.log('Delete event:', event);
  };
  
  // Enhanced permissions that include manager access
  const permissions = {
    canCreate: isAdmin || canManageTeam,
    canUpdate: isAdmin || canManageTeam,
    canDelete: isAdmin || canManageTeam,
    canDrag: isAdmin || canManageTeam
  };
  
  
  const renderViewModeName = () => {
    if (isGuard) {
      return t("week") || "Week";
    }
    
    switch (viewMode) {
      case 'day':
        return t("day") || "Day";
      case 'week':
        return t("week") || "Week";
      case 'month':
        return t("month") || "Month";
      case 'custom':
        return t("custom") || "Custom";
      case 'total-work':
        return t("totalWork") || "Total Work";
      default:
        return t("week") || "Week";
    }
  };
  
  return (
    <AppLayout>
      <div className="container px-2 md:px-6 pb-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">{t('guardTeamSchedule') || "Guard Team Schedule"}</h1>
        </div>
        
        <Card className="bg-background shadow-sm">
          <CardContent className="p-0">
            <div className="p-4 sm:p-6">
              {isGuard ? (
                <div className="flex flex-col space-y-2 pb-1 md:space-y-0 md:flex-row justify-between w-full">
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleNavigatePrevious}
                      className="h-8 text-xs sm:text-sm border-dashed"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="sr-only md:not-sr-only md:ml-1">
                        {t("previous") || "Previous"}
                      </span>
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleGoToToday}
                      className="h-8 text-xs sm:text-sm whitespace-nowrap"
                    >
                      {t("today") || "Today"}
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleNavigateNext}
                      className="h-8 text-xs sm:text-sm border-dashed"
                    >
                      <span className="sr-only md:not-sr-only md:mr-1">
                        {t("next") || "Next"}
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    
                    <div className="hidden md:flex items-center ml-2">
                      <Calendar className="h-4 w-4 mr-1 text-primary" />
                      <span className="font-medium text-sm">
                        {format(currentDate, 'MMMM yyyy')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 w-full justify-between md:justify-end md:w-auto">
                    <div className="flex md:hidden items-center">
                      <Calendar className="h-4 w-4 mr-1 text-primary" />
                      <span className="font-medium text-sm">
                        {format(currentDate, 'MMM yyyy')}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="h-8 text-xs sm:text-sm pointer-events-none"
                      >
                        <CalendarDays className="h-3.5 w-3.5 mr-1" />
                        {renderViewModeName()}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <CalendarToolbar
                  isAdmin={isAdmin || canManageTeam}
                  viewMode={viewMode}
                  currentDate={currentDate}
                  onNavigatePrevious={handleNavigatePrevious}
                  onNavigateNext={handleNavigateNext}
                  onGoToToday={handleGoToToday}
                  onViewModeChange={setViewMode}
                  isExporting={isExporting}
                  guards={[]}
                  selectedGuardId="all"
                  handleGuardSelect={() => {}}
                />
              )}
            </div>
            
            <div className="h-[calc(100vh-280px)] overflow-auto px-4 pb-4">
              <CalendarViewSwitcher
                viewMode={viewMode}
                currentDate={currentDate}
                filteredSchedules={filteredSchedules}
                dateRange={dateRange}
                isCustomView={isCustomView}
                isAdmin={isAdmin || canManageTeam}
                guards={[]}
                selectedGuardId="all"
                guardSchedules={guardSchedules}
                allGuardsSchedules={[]}
                isLoadingGuardSchedules={isLoadingGuardSchedules}
                user={null}
                permissions={permissions}
                handleDayClick={handleDayClick}
                handleEventClick={handleEventClick}
                onDuplicateEvent={handleDuplicateEvent}
                onDeleteEvent={handleDeleteEvent}
                handleGuardSelect={() => {}}
                onDragEnd={handleDragEnd}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

const GuardTeamSchedule = () => {
  return (
    <ColorIntensityProvider>
      <GuardTeamScheduleContent />
    </ColorIntensityProvider>
  );
};

export default GuardTeamSchedule;
